import { IOption } from "component-library";

import PageKey from "config/PageKey";

import {
  IBreakdownPageTranslation,
  ICommonTranslation,
} from "models/translation";

import defaultsWithFallback from "helpers/defaultsWithFallback";
import { ListItemTranslator } from "helpers/translationHelpers";
import { fallbackTranslation } from "services/api/fallbackData";

import { WhichCVType } from "hoc/withMakeSuggestionsFilter/withMakeSuggestionsFilter.config";

export interface IMakeSuggestionsCardTexts {
  voiceClipsTooltip: string;
  videoShowreelTooltip: string;
  membershipTypes: {
    performer: string;
    graduate: string;
    youngPerformer: string;
    presenter: string;
    dancer: string;
  };
  performerAge: string;
  morePhotos: string;
  membershipExpires: string;
  addComment: string;
  clientNudged: string;
  clientSubmittedThemselves: string;
}

export interface IMakeSuggestionsGridTexts extends IMakeSuggestionsCardTexts {
  resultStats: string;
}

export interface ISuggestionsViewTexts extends IMakeSuggestionsGridTexts {
  cancel: string;
  editSuggestion: string;
  remove: string;
  submit: string;
  suggest: string;
  cvOptions: IOption<string>[];
  agenciesFilter: string;
  amendment: string;
  contractExpiryWarning: string;
  cvCount: string;
  inResponse: string;
  noClientsMessage: string;
  noClientsTitle: string;
  noSuggestions: string;
  roleClosed: string;
  searchPerformer: string;
  suggestClients: string;
  suggestionsSent: string;
  titleAgency: string;
  titleClient: string;
  less: string;
  more: string;
  isRoleCastButtonText: string;
}

export const cvOptionLabelKeys = {
  currentSuggestions: "current-suggestions",
  nudges: "nudges",
  selfSuggestions: "self-suggestions",
  allProfiles: "all-profiles",
};

export const membershipTypesKeys = {
  performer: "Performer",
  graduate: "Graduate",
  youngPerformer: "YoungPerformer",
  presenter: "Presenter",
  dancer: "Dancer",
};

const fallbackPageTranslation = fallbackTranslation[PageKey.Breakdown];

export function prepareTexts(
  pageTranslation: IBreakdownPageTranslation,
  commonTranslation: ICommonTranslation
): ISuggestionsViewTexts {
  const texts = defaultsWithFallback(
    pageTranslation?.viewSuggestions,
    fallbackPageTranslation.viewSuggestions
  );

  const membershipTranslator = new ListItemTranslator(
    pageTranslation?.viewSuggestions?.viewSuggestionsMembershipTypes,
    fallbackPageTranslation.viewSuggestions.viewSuggestionsMembershipTypes
  );

  return {
    cancel: texts.viewSuggestionsActionCancel,
    editSuggestion: texts.viewSuggestionsActionEditSuggestion,
    remove: texts.viewSuggestionsActionRemove,
    submit: texts.viewSuggestionsActionSubmit,
    suggest: texts.viewSuggestionsActionSuggest,
    resultStats: commonTranslation.grid.resultStats,
    cvOptions: prepareCVOptions(pageTranslation),
    addComment: texts.viewSuggestionsLabelAddComment,
    agenciesFilter: texts.viewSuggestionsLabelAgenciesFilter,
    amendment: texts.viewSuggestionsLabelAmendment,
    contractExpiryWarning: texts.viewSuggestionsLabelContractExpiryWarning,
    cvCount: texts.viewSuggestionsLabelSelectedCount,
    inResponse: texts.viewSuggestionsLabelInResponse,
    less: texts.viewSuggestionsLabelLess,
    more: texts.viewSuggestionsLabelMore,
    morePhotos: texts.viewSuggestionsLabelMorePhotos,
    noClientsMessage: texts.viewSuggestionsLabelNoClientsMessage,
    noClientsTitle: texts.viewSuggestionsLabelNoClientsTitle,
    noSuggestions: texts.viewSuggestionsLabelNoSuggestions,
    performerAge: texts.viewSuggestionsLabelPerformerAge,
    roleClosed: texts.viewSuggestionsLabelRoleClosed,
    searchPerformer: texts.viewSuggestionsLabelSearchPerformer,
    suggestClients: texts.viewSuggestionsLabelSuggestClients,
    suggestionsSent: texts.viewSuggestionsLabelSuggestionsSent,
    titleAgency: texts.viewSuggestionsTitleAgency,
    titleClient: texts.viewSuggestionsTitleClient,
    voiceClipsTooltip: texts.viewSuggestionsVoiceClipsTooltip,
    videoShowreelTooltip: texts.viewSuggestionsVideoShowreelTooltip,
    membershipTypes: {
      performer: membershipTranslator.get(membershipTypesKeys.performer),
      graduate: membershipTranslator.get(membershipTypesKeys.graduate),
      youngPerformer: membershipTranslator.get(
        membershipTypesKeys.youngPerformer
      ),
      presenter: membershipTranslator.get(membershipTypesKeys.presenter),
      dancer: membershipTranslator.get(membershipTypesKeys.dancer),
    },
    membershipExpires: texts.viewSuggestionsMembershipExpires,
    clientNudged: texts.viewSuggestionsLabelClientNudged,
    clientSubmittedThemselves:
      texts.viewSuggestionsLabelClientSubmittedThemselves,
    isRoleCastButtonText: texts.viewSuggestionsIsRoleCastButtonText,
  };
}

export function prepareCVOptions(
  pageTranslation: IBreakdownPageTranslation
): IOption<string>[] {
  const cvListItemTranslator = new ListItemTranslator(
    pageTranslation?.viewSuggestions?.viewSuggestionsCvOptions,
    fallbackPageTranslation.viewSuggestions.viewSuggestionsCvOptions
  );

  return [
    {
      name: cvListItemTranslator.get(cvOptionLabelKeys.allProfiles),
      value: WhichCVType.AllProfiles,
    },
    {
      name: cvListItemTranslator.get(cvOptionLabelKeys.currentSuggestions),
      value: WhichCVType.CurrentSuggestions,
    },
    {
      name: cvListItemTranslator.get(cvOptionLabelKeys.nudges),
      value: WhichCVType.Nudges,
    },
    {
      name: cvListItemTranslator.get(cvOptionLabelKeys.selfSuggestions),
      value: WhichCVType.SelfSuggestions,
    },
  ];
}
